import { useCallback, useEffect } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure'
import { showError, showSuccess } from '../../shared/ui/appToast';
import { useSyncBranchSettingsOnFocus } from '../../shell/session/hooks/useSyncBranchSettingsOnFocus'
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission'
import { useModulePermission } from '../../shell/session/hooks/useModulePermission'
import { useSessionStore } from '../../shell/session/store/useSessionStore'
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities'
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible'
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider'
import { useResetModuleUiOnBlur } from '../../shell/navigation/useResetModuleUiOnBlur';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { resetCatalogTabOverlays, useCatalogOverlayStore } from '../store/useCatalogOverlayStore';
import {
  currencyFormatter,
} from './catalogScreen.constants';
import { statusFilters, sortOptions } from '../domain/catalogFilterOptions';
import { formatUpdatedAt } from './catalogScreen.utils';
import { useProductVariantsEnabled } from '../application/useProductVariantsEnabled'
import { bulkDeactivateProducts, bulkReactivateProducts, updateProductAvgCost } from '../infra/productsRepo';
import { useCatalogListVm } from './viewModels/catalogListVm';
import { useCatalogFormVm } from './viewModels/catalogFormVm';
import { useCatalogActionsVm } from './viewModels/catalogActionsVm';
import { useCatalogKpis } from '../application/useCatalogKpis';
import { catalogChromeCopy } from '../domain/catalogTerminology';
import { getStockByProductAndBranch } from '../../inventory/public-api';

export const useCatalogScreen = () => {
  useSyncBranchSettingsOnFocus();

  const { canEdit } = useBranchPermission('Catalog');
  const canEditPrice = useModulePermission('Catalog', 'edit_price');
  const canEditCost = useModulePermission('Catalog', 'edit_cost');
  const canPublish = useModulePermission('Catalog', 'publish');
  const canImportExport = useModulePermission('Catalog', 'import_export');
  const canManageMasters = useModulePermission('Catalog', 'manage_masters');
  const { entitlements: planEnt, loading: planLoading } = usePlanEntitlements();
  const user = useAuthStore((state) => state.user);
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branches = useSessionStore((state) => state.branches);
  const currentBranch = branches.find((branch) => branch.id === currentBranchId);

  const serviceCatalogEnabled = useHasBusinessCapability('service_catalog');
  const storefrontEnabled = usePublicStoreFeaturesVisible();
  const appointmentsEnabled = useHasBusinessCapability('appointments');
  const inventoryEnabled = useHasBusinessCapability('inventory');
  // Farmacia: capability toggleable (default ON en pharmacy_basic), no fork por rubro.
  const pharmacyFieldsEnabled = useHasBusinessCapability('product_pharmacy_fields');
  const variantsEnabled = useProductVariantsEnabled();
  // Stock panels follow inventory cap; service rows still hide via itemType checks in form/detail.
  const hideStockPanels = !inventoryEnabled;
  const chromeCopy = catalogChromeCopy(serviceCatalogEnabled, storefrontEnabled);

  const getStockByProductAndBranchStable = useCallback(
    (branchId: string, productId: string) => getStockByProductAndBranch(branchId, productId),
    [],
  );

  const overlay = useCatalogOverlayStore();
  const {
    modalVisible,
    setModalVisible,
    saving,
    setSaving,
    editingProduct,
    setEditingProduct,
    formMode,
    setFormMode,
    draft,
    setDraft,
    searchScannerVisible,
    setSearchScannerVisible,
    formScannerVisible,
    setFormScannerVisible,
    lookupFeedback,
    setLookupFeedback,
    remoteSuggestion,
    setRemoteSuggestion,
    formSuggestion,
    setFormSuggestion,
    selectedProduct,
    setSelectedProduct,
    priceMarginPercentInput,
    setPriceMarginPercentInput,
    formCompact,
    setFormCompact,
  } = overlay;

  // ── KPIs: globales del catálogo, independientes de filtros ──
  const kpis = useCatalogKpis();

  // ── List VM: filters, categories, master options, import, navigation ──
  const listVm = useCatalogListVm({
    serviceCatalogEnabled,
    draft,
    setDraft,
    canEdit,
    maxProducts: planEnt?.max_products ?? null,
    performedByUserId: user?.id,
  });

  useEffect(() => {
    if (hideStockPanels && listVm.alertFilter) {
      listVm.setAlertFilter(false);
    }
  }, [hideStockPanels, listVm.alertFilter, listVm.setAlertFilter]);

  // ── Form VM: create/edit/duplicate, validation, detail pane, stock ──
  const formVm = useCatalogFormVm({
    canEdit,
    user,
    planEnt,
    appointmentsEnabled,
    planLoading,
    currentBranchId,
    loadFirstPage: async () => {
      await listVm.loadFirstPage();
      void kpis.refresh();
    },
    defaultCategorySlugForNewItem: listVm.catalogDefaultCategoryForNewItem,
    defaultItemTypeForNew: listVm.defaultItemTypeForNew,
    variantsEnabled,
    refreshCatalogMasterOptions: listVm.refreshCatalogMasterOptions,
    draft,
    setDraft,
    editingProduct,
    setEditingProduct,
    selectedProduct,
    setSelectedProduct,
    saving,
    setSaving,
    modalVisible,
    setModalVisible,
    formMode,
    setFormMode,
    formSuggestion,
    setFormSuggestion,
    remoteSuggestion,
    setRemoteSuggestion,
    priceMarginPercentInput,
    setPriceMarginPercentInput,
    formCompact,
    setFormCompact,
    getCategoryLabel: listVm.getCategoryLabel,
    categoryOptions: listVm.formCategoryOptions,
    getStockByProductAndBranch: getStockByProductAndBranchStable,
  });

  // ── Actions VM: confirm flow, bulk price, barcode, history ──
  const actionsVm = useCatalogActionsVm({
    canEdit,
    user,
    currentBranchId,
    loadFirstPage: async () => {
      await listVm.loadFirstPage();
      void kpis.refresh();
    },
    statusFilter: listVm.statusFilter,
    categoryFilter: listVm.categoryFilter,
    itemTypeFilter: listVm.itemTypeFilter,
    searchQuery: listVm.searchQuery,
    setSearchQuery: listVm.setSearchQuery,
    setStatusFilter: listVm.setStatusFilter,
    setCategoryFilter: listVm.setCategoryFilter,
    setItemTypeFilter: listVm.setItemTypeFilter,
    setSelectedProduct,
    setSearchScannerVisible,
    setFormScannerVisible,
    setLookupFeedback,
    setRemoteSuggestion,
    setFormSuggestion,
    setDraft,
    editingProduct,
    draft,
    clearCatalogSearchPin: listVm.clearPinnedCatalogSearch,
    openDuplicateModal: formVm.openDuplicateModal,
    getProductStockCheck: formVm.getProductStockCheck,
  });

  useResetModuleUiOnBlur(
    useCallback(() => {
      resetCatalogTabOverlays();
      actionsVm.resetBulkPrice();
      formVm.closeProductDetail();
      actionsVm.closeProductHistory();
      actionsVm.closeProductConfirm();
      formVm.closeModal();
    }, [
      actionsVm.resetBulkPrice,
      formVm.closeModal,
      actionsVm.closeProductConfirm,
      formVm.closeProductDetail,
      actionsVm.closeProductHistory,
    ])
  );

  const handleInlineCostEdit = useCallback(
    async (productId: string, newCost: number) => {
      if (!canEdit) return;
      try {
        await updateProductAvgCost(productId, newCost, { performedByUserId: user?.id ?? null });
        await listVm.handleRefreshAfterLocalEdit();
        void kpis.refresh();
        showSuccess('Costo actualizado', 'El costo promedio se actualizó correctamente.');
      } catch (e) {
        showError('Error', 'No se pudo actualizar el costo.');
      }
    },
    [canEdit, user?.id, listVm.handleRefreshAfterLocalEdit, kpis]
  );

  const handleBulkActivate = useCallback(
    async (productIds: string[]) => {
      if (!canEdit || productIds.length === 0) return;
      try {
        await bulkReactivateProducts(productIds, { performedByUserId: user?.id ?? null });
        await listVm.loadFirstPage();
        void kpis.refresh();
        showSuccess('Productos activados', `${productIds.length} producto${productIds.length === 1 ? '' : 's'} activado${productIds.length === 1 ? '' : 's'}.`);
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'catalog', op: 'error-al-activar', error: e, title: 'Error al activar', userMessage: 'No se pudieron activar todos los productos.' });
      }
    },
    [canEdit, user?.id, listVm.loadFirstPage, kpis]
  );

  const handleBulkDeactivate = useCallback(
    async (productIds: string[]) => {
      if (!canEdit || productIds.length === 0) return;
      try {
        await bulkDeactivateProducts(productIds, { performedByUserId: user?.id ?? null });
        await listVm.loadFirstPage();
        void kpis.refresh();
        showSuccess('Productos ocultos', `${productIds.length} producto${productIds.length === 1 ? '' : 's'} oculto${productIds.length === 1 ? '' : 's'}.`);
      } catch (e: any) {
        presentUnexpectedFailure({ module: 'catalog', op: 'error-al-ocultar', error: e, title: 'Error al ocultar', userMessage: 'No se pudieron ocultar todos los productos.' });
      }
    },
    [canEdit, user?.id, listVm.loadFirstPage, kpis]
  );

  return {
    canEdit,
    currentBranchId,
    currentBranch,
    branches,
    // list VM
    ...listVm,
    // overlay state
    modalVisible,
    saving,
    editingProduct,
    formMode,
    draft,
    setDraft,
    searchScannerVisible,
    setSearchScannerVisible,
    formScannerVisible,
    setFormScannerVisible,
    lookupFeedback,
    remoteSuggestion,
    formSuggestion,
    selectedProduct,
    priceMarginPercentInput,
    setPriceMarginPercentInput,
    // form VM
    ...formVm,
    // actions VM
    ...actionsVm,
    // misc
    planEnt,
    planLoading,
    setLookupFeedback,
    setFormSuggestion,
    setSelectedProduct,
    currencyFormatter,
    formatUpdatedAt,
    statusFilters,
    sortOptions,
    user,
    serviceCatalogEnabled,
    pharmacyFieldsEnabled,
    hideStockPanels,
    chromeCopy,
    variantsEnabled,
    canEditPrice,
    canEditCost,
    canPublish,
    canImportExport,
    canManageMasters,
    handleBulkActivate,
    handleBulkDeactivate,
    handleInlineCostEdit,
    // KPIs
    kpis: kpis.totalProducts,
    kpisOnMenu: kpis.onMenuCount,
    kpisServices: kpis.servicesCount,
    kpisAlerts: kpis.alertsCount,
    kpisIncomplete: kpis.incompleteCount,
    kpisLoading: kpis.loading,
  };
};
